import { t } from '@lingui/macro';

import { AdminPages } from '../AdminPages';
import { buildRootPageTarget } from '../AdminLinkUtils';
import { SORT_ASC, SORT_DESC } from '../../../utilities/FormUtilities';
import {
    VOIP_MODE_NONE,
    VOIP_MODE_PRE_AUTO,
    VOIP_MODE_PRE_MANUAL,
    VOIP_MODE_PRE_PROVISIONED,
} from '../agents-voip/AgentVoIPConstants';

export const VOIP_AGENTS_API_URL = '/ipm/v1/admin/agents/';
export const VOIP_CALL_MANAGERS_API_URL = '/ipm/v1/admin/voipcallmanagers';
export const VOIP_CALL_MANAGERS_USAGE_COUNT_API_URL = '/ipm/v1/admin/agents/agentCountForCallManagerSelection';

export const VOIP_ROOT = buildRootPageTarget(AdminPages.VoipConfig);
export const VOIP_LIST_URL = `${VOIP_ROOT}/list`;
export const VOIP_CALL_MANAGERS_ROOT = `${VOIP_ROOT}/callManagers`;

export const VOIP_PHONE_REGEX = /^[\d\-().+\s]+$/i;

export const VOIP_DEFAULT_VALUES = {
    voipMode: VOIP_MODE_NONE,
};

export const VOIP_MODE_OPTIONS_LEGACY = [
    { value: VOIP_MODE_PRE_AUTO, label: t`Auto` },
    { value: VOIP_MODE_PRE_MANUAL, label: t`Manual` },
    { value: VOIP_MODE_NONE, label: t`None` },
];

export const VOIP_MODE_OPTIONS_ISNG = [
    { value: VOIP_MODE_PRE_MANUAL, label: t`Manual` },
    { value: VOIP_MODE_NONE, label: t`None` },
];
export const VOIP_VLAN_STATUS_OPTIONS = [
    { value: 'A', label: t`Auto` },
    { value: 'M', label: t`Manual` },
    { value: 'N', label: t`None` },
];
export const VOIP_VLAN_ISNG_STATUS_OPTIONS = [
    { value: 'M', label: t`Manual` },
    { value: 'N', label: t`None` },
];
export const VOIP_ADAPTER_TYPE = {
    default: 'default',
    wireless: 'wireless',
};
export const VOIP_ADAPTER_TYPE_OPTIONS = [
    { value: VOIP_ADAPTER_TYPE.default, label: t`Wired Interface` },
    { value: VOIP_ADAPTER_TYPE.wireless, label: t`Wireless Interface` },
];

export const VOIP_IP_VERSION_OPTIONS = [
    { value: 'ipv4', label: t`IPv4` },
    { value: 'ipv6', label: t`IPv6` },
];
export const VOIP_QOS_MODE = {
    default: 'default',
    dscp: 'dscp',
    ip: 'ip',
};
export const VOIP_QOS_MODE_OPTIONS = [
    { value: VOIP_QOS_MODE.default, label: t`Default` },
    { value: VOIP_QOS_MODE.dscp, label: t`ToS with DSCP` },
    { value: VOIP_QOS_MODE.ip, label: t`ToS with IP Precedence` },
];
export const VOIP_DSCP_OPTIONS = [
    { value: 0, label: t`Default Forwarding (0)` },
    { value: 10, label: t`Assured Forwarding 11 (10)` },
    { value: 12, label: t`Assured Forwarding 12 (12)` },
    { value: 14, label: t`Assured Forwarding 13 (14)` },
    { value: 18, label: t`Assured Forwarding 21 (18)` },
    { value: 20, label: t`Assured Forwarding 22 (20)` },
    { value: 22, label: t`Assured Forwarding 23 (22)` },
    { value: 26, label: t`Assured Forwarding 31 (26)` },
    { value: 28, label: t`Assured Forwarding 32 (28)` },
    { value: 30, label: t`Assured Forwarding 33 (30)` },
    { value: 34, label: t`Assured Forwarding 41 (34)` },
    { value: 36, label: t`Assured Forwarding 42 (36)` },
    { value: 38, label: t`Assured Forwarding 43 (38)` },
    { value: 44, label: t`Voice Admit (44)` },
    { value: 46, label: t`Expedited Forwarding (46)` },
];
export const VOIP_IP_PRECEDENCE_OPTIONS = [
    { value: 0, label: t`Routine (0)` },
    { value: 1, label: t`Priority (1)` },
    { value: 2, label: t`Excellent Effort (2)` },
    { value: 3, label: t`Flash (3)` },
    { value: 4, label: t`Flash Override (4)` },
    { value: 5, label: t`Critical/ECP (5)` },
    { value: 6, label: t`Internetwork Control (6)` },
    { value: 7, label: t`Network Control (7)` },
];
export const VOIP_TOS_CODE = {
    normal: 0,
    delay: 1,
    throughput: 2,
    reliability: 3,
};
export const VOIP_TOS_CODE_OPTIONS = [
    { value: VOIP_TOS_CODE.normal, label: t`Normal` },
    { value: VOIP_TOS_CODE.delay, label: t`Delay` },
    { value: VOIP_TOS_CODE.throughput, label: t`Throughput` },
    { value: VOIP_TOS_CODE.reliability, label: t`Reliability` },
];

export const VOIP_HARDWARE_ONLY_MODES = [VOIP_MODE_PRE_AUTO, VOIP_MODE_PRE_PROVISIONED];

export const VOIP_CALL_MANAGER_TYPE_OTHER = 'other';
export const VOIP_CALL_MANAGER_TYPE_CUCM = 'cucm';

export const VOIP_CALL_MANAGER_TYPE_OPTIONS = [
    { value: VOIP_CALL_MANAGER_TYPE_OTHER, label: t`Other` },
    { value: VOIP_CALL_MANAGER_TYPE_CUCM, label: t`Cisco Unified CM` },
];

export const VOIP_CALL_MANAGER_DEFAULT_VALUES = {
    cmType: VOIP_CALL_MANAGER_TYPE_CUCM,
    sipPort: 5060,
    verifyCMCertificate: true,
};

export const VOIP_AGENT_LIST_REFRESH_INTERVAL = 20000;
export const VOIP_AGENT_LIST_INITIAL_SORT = [
    { property: 'voipConfiguration.registration.updatedAt', direction: SORT_DESC },
];
export const VOIP_CALL_MANAGERS_INITIAL_SORT = [{ property: 'name', direction: SORT_ASC }];

export const VOIP_AGENT_LIST_DATA_SOURCE = {
    url: '/query/admin/agents',
    crudUrl: '/ipm/v1/admin/agents',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [
            'name',
            'domain',
            'sipPort',
            'description',
            'status',
            'translatedType',
            'modelShortDisplay',
            'voipMode',
            'tags',
            'phoneNumber',
            'sipUserId',
            'voipConfiguration.extension',
            'voipConfiguration.registration.status',
            'voipConfiguration.primaryCallManagerName',
            'voipConfiguration.ipVersion',
        ],
    },
};

export const VOIP_CALL_MANAGERS_LIST_DATA_SOURCE = {
    url: '/ipm/v1/admin/voipcallmanagers',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        entityFields: ['name', 'domain', 'sipProxy', 'description'],
    },
};
